import type { TableProps } from 'antd';

import { Table } from 'antd';
import classNames from 'classnames';
import ResizeObserver from 'rc-resize-observer';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { VariableSizeGrid as Grid, GridOnScrollProps } from 'react-window';

import styles from './VirtualTable.module.less';

const DEFAULT_TABLE_WIDTH = 800; // It break jest tests if table with is not set by default
const DEFAULT_ROW_HEIGHT = 54;

type VirtualTableProps<T> = TableProps<T> & {
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetching?: boolean;
  loadMoreLabel?: string;
};

const GridCell = <RecordType extends object>({
  columnIndex,
  rowIndex,
  style,
  data,
  mergedColumns,
  loadMoreLabel,
  setRowHeight,
}: {
  columnIndex: number;
  rowIndex: number;
  style: React.CSSProperties;
  data: RecordType[];
  mergedColumns: TableProps<RecordType>['columns'];
  loadMoreLabel?: string;
  setRowHeight: (index: number, height: number) => void;
}) => {
  const rowRef = useRef<HTMLDivElement>(null);

  const onCellMouseEnter = (lineIndex: number) => {
    const cells = document.getElementsByClassName(`virtual-table-cell-${lineIndex}`);
    if (cells.length > 0) {
      Array.from(cells).forEach((cell) => {
        cell.classList.add(styles.virtualTableCellHovered);
      });
    }
  };

  const onCellMouseLeave = (lineIndex: number) => {
    const cells = document.getElementsByClassName(`virtual-table-cell-${lineIndex}`);
    if (cells.length > 0) {
      Array.from(cells).forEach((cell) => {
        cell.classList.remove(styles.virtualTableCellHovered);
      });
    }
  };

  useEffect(() => {
    if (rowRef.current && columnIndex === 0) {
      setRowHeight(
        rowIndex,
        rowRef.current.clientHeight > DEFAULT_ROW_HEIGHT
          ? rowRef.current.clientHeight
          : DEFAULT_ROW_HEIGHT,
      );
    }
  }, [rowRef]);

  if (rowIndex === data.length) {
    if (columnIndex > 0) {
      return <></>;
    }

    return (
      <div
        className={classNames(
          'virtual-table-cell',
          `virtual-table-cell-${rowIndex}`,
          {
            'virtual-table-cell-last': mergedColumns && columnIndex === mergedColumns.length - 1,
          },
          styles.virtualTableCellContainer,
        )}
        data-testid="virtual-table-loader"
        style={style}
      >
        <div className={styles.virtualTableCellWrapper}>
          <div className={styles.virtualTableCell}>{loadMoreLabel}</div>
        </div>
      </div>
    );
  }

  const currentColumn = (mergedColumns as any)[columnIndex];
  const attributes = currentColumn.dataIndex.split('.');

  let propertyValue;

  if (attributes.length > 1) {
    propertyValue = (data[rowIndex] as any)[attributes[0]][attributes[1]];
  } else {
    propertyValue = (data[rowIndex] as any)[currentColumn.dataIndex];
  }

  return (
    <div
      className={classNames(
        'virtual-table-cell',
        `virtual-table-cell-${rowIndex}`,
        {
          'virtual-table-cell-last': mergedColumns && columnIndex === mergedColumns.length - 1,
        },
        styles.virtualTableCellContainer,
      )}
      data-testid={`virtual-table-row-${currentColumn.key}-${(data[rowIndex] as any).id}`}
      style={style}
      onMouseEnter={() => onCellMouseEnter(rowIndex)}
      onMouseLeave={() => onCellMouseLeave(rowIndex)}
    >
      <div ref={rowRef} className={styles.virtualTableCellWrapper}>
        <div className={styles.virtualTableCell}>
          {currentColumn.render ? currentColumn.render(propertyValue) : propertyValue}
        </div>
      </div>
    </div>
  );
};

export const VirtualTable = <RecordType extends object>(props: VirtualTableProps<RecordType>) => {
  const { columns, scroll, isFetching, fetchNextPage, hasNextPage, loadMoreLabel, dataSource } =
    props;
  const [tableWidth, setTableWidth] = useState(DEFAULT_TABLE_WIDTH);
  const [totalHeight, setTotalHeight] = useState((dataSource?.length || 1) * DEFAULT_ROW_HEIGHT);
  const [isScrollBottomReached, setScrollBottomReached] = useState(false);

  const rowHeights = useRef<any>({});
  const gridRef = useRef<any>(undefined);

  const widthColumnCount = columns!.filter(({ width }) => !width).length;
  const widthColumnTotal =
    columns?.filter(({ width }) => width).reduce((acc, cur) => acc + (cur.width as number), 0) || 0;
  const mergedColumns = columns!.map((column) => {
    if (column.width) {
      return column;
    }

    return {
      ...column,
      width: Math.floor((tableWidth - widthColumnTotal) / widthColumnCount),
    };
  });

  const resetVirtualGrid = () => {
    rowHeights.current = {};
    gridRef.current?.resetAfterIndices({
      columnIndex: 0,
      shouldForceUpdate: true,
    });
  };

  useEffect(() => resetVirtualGrid, [tableWidth]);

  useEffect(() => {
    if (isScrollBottomReached && !isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetching, isScrollBottomReached]);

  // We use scroll position to determine if next page must be fetched.
  const onScroll = (scrollProps: GridOnScrollProps) => {
    const lastScroll = totalHeight + DEFAULT_ROW_HEIGHT - (props.scroll?.y as number);
    if (lastScroll > 0 && scrollProps.scrollTop >= lastScroll && hasNextPage) {
      setScrollBottomReached(true);
    } else {
      setScrollBottomReached(false);
    }
  };

  const outerElementType = useMemo(
    () =>
      forwardRef<HTMLDivElement>((outerProps, outerRef) => (
        <div ref={outerRef} {...outerProps} data-testid="virtual-grid" />
      )),
    [],
  );

  const getRowHeight = (index: number) => rowHeights.current[index] || DEFAULT_ROW_HEIGHT;

  // Each row height is stored in gridRef to be re-rendered at the right size
  const setRowHeight = (index: number, size: number) => {
    gridRef.current?.resetAfterRowIndex(0);
    rowHeights.current = { ...rowHeights.current, [index]: size };
    setTotalHeight(
      dataSource?.length
        ? Object.values(rowHeights.current as object).reduce((total, height) => total + height, 0) +
            (dataSource.length - Object.values(rowHeights.current as object).length) *
              DEFAULT_ROW_HEIGHT
        : 0,
    );
  };

  const renderVirtualList = (
    rawData: RecordType[],
    {
      scrollbarSize,
    }: {
      scrollbarSize: number;
    },
  ) => (
    <Grid
      ref={gridRef}
      className={styles.virtualGrid}
      columnCount={mergedColumns.length}
      columnWidth={(index: number) => {
        const { width } = mergedColumns[index];
        return totalHeight > (scroll!.y as number)! && index === mergedColumns.length - 1
          ? (width as number) - scrollbarSize
          : (width as number);
      }}
      height={totalHeight > (scroll!.y as number)! ? (scroll!.y as number) : totalHeight}
      onScroll={onScroll}
      outerElementType={outerElementType}
      rowCount={rawData.length + (hasNextPage ? 1 : 0)}
      rowHeight={getRowHeight}
      width={tableWidth}
    >
      {({
        columnIndex,
        rowIndex,
        style,
      }: {
        columnIndex: number;
        rowIndex: number;
        style: React.CSSProperties;
      }) => (
        <GridCell<RecordType>
          columnIndex={columnIndex}
          rowIndex={rowIndex}
          style={style}
          data={rawData}
          mergedColumns={mergedColumns}
          loadMoreLabel={loadMoreLabel}
          setRowHeight={setRowHeight}
        />
      )}
    </Grid>
  );

  return (
    <ResizeObserver
      onResize={({ width }) => {
        setTableWidth(width);
      }}
    >
      <Table
        {...props}
        className="virtual-table"
        columns={mergedColumns}
        pagination={false}
        scroll={props.loading || !props.dataSource?.length ? { x: 0 } : props.scroll} // Disable horizontal scroll on loading
        components={
          props.dataSource?.length
            ? {
                body: renderVirtualList,
              }
            : undefined
        }
      />
    </ResizeObserver>
  );
};
